<template>
  <div>
    <div class="row">
      <ul
        class="StepsNavSimple__list shared"
        :style="sharedStyle"
      >
        <li
          v-for="stepItem in sharedSteps"
          :key="stepItem.uuid"
          class="StepsNavSimple__item"
        >
          <div
            class="SNOrb"
            :class="orbClass(stepItem)"
            @mouseover="hover = stepItem.uuid"
            @mouseleave="hover = null"
          >
            <span class="SNOrb__label">
              <b-badge variant="dark">{{ stepItem.short }}. {{ label(stepItem) }}</b-badge>
            </span>
            <span
              class="SNOrb__step"
              @click="navigate(stepItem)"
            >{{ stepItem.short }}</span>
          </div>
        </li>
      </ul>

      <div
        class="branches"
        :style="branchesStyle"
      >
        <span
          class="branch-line"
          :class="{
            'branch-line--active': branchLineActive,
            'branch-line--faded': !branchLineActive && activeBranch && activeBranch !== branches[1]?.key
          }"
        />
        <div
          v-for="branch in branches"
          :key="branch.key"
          class="branch"
          :class="{
            active: activeBranch === branch.key,
            inactive: activeBranch !== null && activeBranch !== branch.key,
            longest: branch.steps.length === maxBranchLength
          }"
        >
          <span class="label">{{ branchLabel(branch.key) }}</span>
          <ul
            class="StepsNavSimple__list"
            :style="{ flex: branch.steps.length === maxBranchLength ? 2 * branch.steps.length - 1 : 2 * branch.steps.length }"
          >
            <li
              v-for="stepItem in branch.steps"
              :key="stepItem.uuid"
              class="StepsNavSimple__item"
            >
              <div
                class="SNOrb"
                :class="orbClass(stepItem)"
                @mouseover="hover = stepItem.uuid"
                @mouseleave="hover = null"
              >
                <span class="SNOrb__label">
                  <b-badge variant="dark">{{ stepItem.short }}. {{ label(stepItem) }}</b-badge>
                </span>
                <span
                  class="SNOrb__step"
                  @click="navigate(stepItem)"
                >{{ stepItem.short }}</span>
              </div>
            </li>
          </ul>
          <div
            v-if="maxBranchLength > branch.steps.length"
            :style="{ flex: 2 * (maxBranchLength - branch.steps.length) - 1 }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { evaluateCondition, valuesLookup } from '@/helpers/evaluateCondition'

export default {
  name: 'StepsNavFork',
  props: {
    step: { type: [String, Number], default: 1 },
    steps: { type: Array, default: () => [] },
    record: { type: Object, required: true }
  },
  data() {
    return { hover: null, activeStep: null }
  },
  computed: {
    sharedSteps() {
      return this.steps.filter(s => !s.branch)
    },
    branches() {
      const groups = {}
      this.steps
        .filter(s => s.branch)
        .forEach(s => {
          if (!groups[s.branch]) groups[s.branch] = []
          groups[s.branch].push(s)
        })
      return Object.entries(groups).map(([key, steps]) => ({
        key,
        steps: steps.slice().sort((a, b) => Number(a.short) - Number(b.short))
      }))
    },
    activeBranch() {
      const source = this.$store.getters['tenant/getBranchSource']
      if (!source) return null
      const currentLocation = this.record?.CurrentLocation
      if (!currentLocation) return null
      const value = source.field.split('.').reduce((obj, k) => obj?.[k], currentLocation)
      return source.map?.[value] || null
    },
    liveSkippedUuids() {
      return this.$store.getters['processes/getSkippedStepUuids'] || []
    },
    savedSkippedUuids() {
      const currentLocation = this.record?.CurrentLocation
      if (!currentLocation) return []
      const activeSteps = this.steps.filter(s => !s.branch || s.branch === this.activeBranch)
      const steps = [...activeSteps].sort((a, b) => Number(a.short) - Number(b.short))
      const skipped = []
      for (let i = 0; i < steps.length; i++) {
        if (skipped.includes(steps[i].uuid)) continue
        const fired = (steps[i].transitions || []).find(({ when }) =>
          evaluateCondition(when, valuesLookup(currentLocation))
        )
        if (!fired) continue
        const targetIdx = steps.findIndex(s => s.uuid === fired.goToStep)
        if (targetIdx <= i) continue
        for (let j = i + 1; j < targetIdx; j++) skipped.push(steps[j].uuid)
      }
      return skipped
    },
    recordState() {
      try { return this.record.flow.state } catch (e) { return [] }
    },
    completedUuids() {
      return this.recordState.filter(s => s.completed_at).map(s => s.uuid)
    },
    availableUuids() {
      return this.recordState.filter(s => s.started_at).map(s => s.uuid)
    },
    routeType() {
      return this.record.processType.value
    },
    sharedStyle() {
      const n = this.sharedSteps.length || 1
      return { flex: `${2 * n - 1} 0 0` }
    },
    branchesStyle() {
      const m = this.maxBranchLength
      return m ? { flex: `${2 * m - 1} 0 0` } : {}
    },
    maxBranchLength() {
      if (!this.branches.length) return 0
      return Math.max(...this.branches.map(b => b.steps.length))
    },
    branchLineActive() {
      const bottomBranch = this.branches[1]
      if (!bottomBranch || this.activeBranch !== bottomBranch.key) return false
      return bottomBranch.steps.some(s => this.availableUuids.includes(s.uuid) || s.uuid === this.activeUuid)
    },
    activeUuid() {
      if (!this.activeStep) return null
      // UUID-based navigation: direct match
      if (this.steps.find(s => s.uuid === this.activeStep)) return this.activeStep
      // Short-number fallback: multiple steps can share a short number across branches
      const matches = this.steps.filter(s => String(s.short) === String(this.activeStep))
      if (matches.length === 1) return matches[0].uuid
      // Narrow to active branch first — never highlight an inactive branch's orb
      const branchMatches = this.activeBranch
        ? matches.filter(s => !s.branch || s.branch === this.activeBranch)
        : matches
      const pool = branchMatches.length ? branchMatches : matches
      // Within the pool, prefer a step that was actually started
      const started = pool.find(s => this.availableUuids.includes(s.uuid))
      return started?.uuid || pool[0]?.uuid || null
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(route) {
        this.activeStep = route.query?.step?.toString()
      }
    }
  },
  methods: {
    label(stepItem) {
      const l = stepItem.label
      if (!l) return ''
      return typeof l === 'object' ? (l[this.$i18n.locale] || l.en || l.nl || '') : l
    },
    branchLabel(key) {
      return this.$t(`processes.nav.branches.${key}`)
    },
    orbClass(stepItem) {
      const isActive = stepItem.uuid === this.activeUuid
      const isCompleted = this.isCompleted(stepItem.uuid)
      const isBranchStep = !!stepItem.branch
      const isInactiveBranch = isBranchStep && stepItem.branch !== this.activeBranch
      // When a branch's own entry point is in the skip list, the whole branch was bypassed.
      // isInactiveBranch already handles dashes for that case — isSkipped must not double-apply.
      // When the branch entry is NOT skipped but a later step within it is, that's a real within-flow skip.
      const branchEntryUuid = isBranchStep
        ? this.branches.find(b => b.key === stepItem.branch)?.steps[0]?.uuid
        : null
      const isBranchEntrySkipped = !!branchEntryUuid && this.isSkipped(branchEntryUuid)
      const isSkipped = this.isSkipped(stepItem.uuid) && !isBranchEntrySkipped

      return {
        'SNOrb--active': isActive,
        jsHover: this.hover === stepItem.uuid,
        'SNOrb--available': !isBranchStep && this.isAvailable(stepItem.uuid),
        'SNOrb--finished': isCompleted && !isInactiveBranch,
        'SNOrb--inactive': isInactiveBranch && !isActive,
        'SNOrb--skipped': isSkipped,
        'SNOrb--skipped-past': isSkipped && this.isSkippedPast(stepItem.uuid)
      }
    },
    isCompleted(uuid) {
      if (this.isSkipped(uuid)) return false
      if (this.savedSkippedUuids.includes(uuid)) return false
      return this.completedUuids.includes(uuid)
    },
    isAvailable(uuid) {
      if (this.liveSkippedUuids.includes(uuid)) return false
      return this.availableUuids.includes(uuid)
    },
    isSkipped(uuid) {
      return this.liveSkippedUuids.includes(uuid)
    },
    isSkippedPast(uuid) {
      const activeSteps = [
        ...this.sharedSteps,
        ...(this.branches.find(b => b.key === this.activeBranch)?.steps || [])
      ].sort((a, b) => Number(a.short) - Number(b.short))
      const idx = activeSteps.findIndex(s => s.uuid === uuid)
      if (idx === -1) return false
      return activeSteps.slice(idx + 1).some(s => this.availableUuids.includes(s.uuid))
    },
    navigate(stepItem) {
      if (!this.isAvailable(stepItem.uuid) && !this.isSkipped(stepItem.uuid)) return
      this.$router.push({
        name: `${this.routeType}Steps`,
        query: { step: stepItem.short }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
$orb-size: 30px;
$orb-center: 30px; // padding-top(15px) + half-orb(15px)
$line-color: $gray-400;
$line-width: 2px;
$label-height: 1.5em;

.row {
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  user-select: none;
}

// All items default to full-width (2 units).
// Edge items are overridden to half-width (1 unit) below.
.StepsNavSimple__item {
  flex: 2 0 0;
}

// Center each orb in its item; ::before = left half-line, ::after = right half-line.
.SNOrb {
  display: flex;
  justify-content: center;

  &::before {
    left: 0;
    width: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: $line-width;
    width: 50%;
    left: calc(50% + 2px);
    background: $line-color;
    z-index: -1;
  }

  &.SNOrb--active .SNOrb__step {
    transform: none;
    margin-left: 0;
  }
}

// Shared steps: dark lines driven by orb state
.shared .SNOrb {
  &.SNOrb--available::after,
  &.SNOrb--finished::after,
  &.SNOrb--active::after { background: $gray-800; }
  &.SNOrb--active::before { background: $gray-800; }
}

// Active branch: dark lines for all progressed states
// Inactive branch is handled entirely by opacity: 0.4 on .branch.inactive
.branch.active .SNOrb {
  &.SNOrb--available::after,
  &.SNOrb--finished::after,
  &.SNOrb--active::after,
  &.SNOrb--skipped-past::after { background: $gray-800; }
  &.SNOrb--active::before,
  &.SNOrb--skipped-past::before { background: $gray-800; }
}

// Step 1: half-width, orb flush left, right line covers full item width
.shared .StepsNavSimple__item:first-child {
  flex: 1 0 0;

  .SNOrb {
    justify-content: flex-start;

    &::before {
      display: none;
    }

    &::after {
      left: 0;
      width: 100%;
    }
  }
}

// Last step of the LONGEST branch only: half-width, orb flush right, left line covers full width.
// This is the globally-last orb (orb 5). Shorter branches' last orbs are normal centered items.
.branch.longest .StepsNavSimple__item:last-child {
  flex: 1 0 0;

  .SNOrb {
    justify-content: flex-end;

    &::before {
      width: 100%;
    }

    &::after {
      display: none;
    }
  }
}

// Shorter branches: last orb is centered but has no right line (nothing beyond it)
.branch:not(.longest) .StepsNavSimple__item:last-child .SNOrb::after {
  display: none;
}

.shared {
  padding-top: 0;
}

.branches {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  padding-top: 0;

}

.branch {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;

  &.inactive {
    pointer-events: none;
    opacity: 0.4;
  }
}

.label {
  position: absolute;
  font-size: 0.6rem;
  font-weight: 600;
  color: $gray-600;
  text-transform: uppercase;
  white-space: nowrap;
  top: 1.25em;
  left: 1em;
}

.branch-line {
  position: absolute;
  left: 0;
  top: $orb-center;
  bottom: $orb-center;
  width: $line-width;
  background: $line-color;
  z-index: -1;
  transition: background 0.1s;

  &--faded {
    background: rgba($line-color, 0.4);
  }

  &--active {
    background: $gray-800;
  }
}
</style>
